import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// all rental main contracts
export const fetchRentalMainContracts = createAsyncThunk(
  "rental_contracts_main/fetchAll",
  async (params = { page: 1, filters: {} }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", params.page);
      queryParams.append("per_page", 20);

      // Append filters - only send non-empty values
      if (params.filters.contract_number) {
        queryParams.append("contract_number", params.filters.contract_number);
      }
      if (params.filters.customer_type) {
        queryParams.append("customer_type", params.filters.customer_type);
      }
      if (params.filters.customer_id) {
        queryParams.append("customer_id", params.filters.customer_id);
      }
      if (params.filters.provider_type) {
        queryParams.append("provider_type", params.filters.provider_type);
      }
      if (params.filters.provider_id) {
        queryParams.append("provider_id", params.filters.provider_id);
      }
      if (params.filters.vehicle_id) {
        queryParams.append("vehicle_id", params.filters.vehicle_id);
      }
      if (params.filters.status) {
        queryParams.append("status", params.filters.status);
      }
      if (Array.isArray(params.filters.rental_projects) && params.filters.rental_projects.length > 0) {
        queryParams.append("rental_projects", JSON.stringify(params.filters.rental_projects));
      }
      if (Array.isArray(params.filters.pickup_cities) && params.filters.pickup_cities.length > 0) {
        queryParams.append("pickup_cities", JSON.stringify(params.filters.pickup_cities));
      }
      if (Array.isArray(params.filters.unloading_cities) && params.filters.unloading_cities.length > 0) {
        queryParams.append("unloading_cities", JSON.stringify(params.filters.unloading_cities));
      }
      if (params.filters.has_driver) {
        queryParams.append("has_driver", params.filters.has_driver);
      }
      if (params.filters.driver_id) {
        queryParams.append("driver_id", params.filters.driver_id);
      }
      if (params.filters.date_filter) {
        queryParams.append("date_filter", params.filters.date_filter);
      }
      if (params.filters.date_filter === "range" && Array.isArray(params.filters.date_value)) {
        if (params.filters.date_value[0] && params.filters.date_value[1]) {
          queryParams.append("date_value[0]", params.filters.date_value[0]);
          queryParams.append("date_value[1]", params.filters.date_value[1]);
        }
      } else if (params.filters.date_value && !Array.isArray(params.filters.date_value)) {
        queryParams.append("date_value", params.filters.date_value);
      }

      const response = await axios.get(`${BASE_URL}/main-contracts?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

// add rental main contracts
export const addRentalMainContracts = createAsyncThunk(
  "rental_contracts_main/add",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/main-contracts`, userData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);
export const mainContractsRecord = createAsyncThunk(
  "rental_contracts_main/mainContractsRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/main-contracts/${id}`);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue({ message });
    }
  }
);

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
  Lang: i18n.language,
});

const extractError = (error, fallback = "An unknown error occurred") =>
  error.response?.data?.message ||
  error.response?.data?.errors?.[Object.keys(error.response?.data?.errors || {})[0]]?.[0] ||
  fallback;

/** إكمال العقد */
export const completeMainContract = createAsyncThunk(
  "rental_contracts_main/complete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/main-contracts/complete/${id}`,
        {},
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  }
);

/** إلغاء العقد */
export const cancelMainContract = createAsyncThunk(
  "rental_contracts_main/cancel",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/main-contracts/cancel/${id}`,
        {},
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  }
);

/** إعادة توليد الطلبات */
export const regenerateMainContractOrders = createAsyncThunk(
  "rental_contracts_main/regenerateOrders",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/main-contracts/${id}/regenerate-orders`,
        {},
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  }
);

/** إنشاء طلب يدوي */
export const createManualRentalOrder = createAsyncThunk(
  "rental_contracts_main/createManualOrder",
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/main-contracts/${id}/manual-order`,
        body,
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  }
);

const RentalMainContractsSlice = createSlice({
  name: "rental_contracts_main",
  initialState: {
    rental_contracts_main: [],
    record: null,
    isLoading: false,
    actionLoading: false,
    error: null,
    success: null,
    successDelete: null,
    errorDelete: null,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRentalMainContracts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRentalMainContracts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rental_contracts_main = action.payload.data.data;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(fetchRentalMainContracts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(addRentalMainContracts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addRentalMainContracts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addRentalMainContracts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(mainContractsRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(mainContractsRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload.data;
      })
      .addCase(mainContractsRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          action.error?.message ||
          "Failed to load data";
      })
      .addCase(completeMainContract.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(completeMainContract.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message;
        if (action.payload?.data) state.record = action.payload.data;
      })
      .addCase(completeMainContract.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to complete contract";
      })
      .addCase(cancelMainContract.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(cancelMainContract.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message;
        if (action.payload?.data) state.record = action.payload.data;
      })
      .addCase(cancelMainContract.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to cancel contract";
      })
      .addCase(regenerateMainContractOrders.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(regenerateMainContractOrders.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message;
      })
      .addCase(regenerateMainContractOrders.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to regenerate orders";
      })
      .addCase(createManualRentalOrder.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createManualRentalOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message;
      })
      .addCase(createManualRentalOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to create manual order";
      });
  },
});

export const { clearState } = RentalMainContractsSlice.actions;
export default RentalMainContractsSlice.reducer;
